// Auth client for the Context app. Context is read-only, so it never requests
// or verifies magic links itself — it only READS the shared, origin-scoped
// session so the header shows the signed-in user, and signs out.
//
// SSO (ADR 0004): the session lives in localStorage, scoped to the origin
// (marketheist.io) not the path, so a user signed into /backtest or /portfolio
// is already signed in here. Sign-in goes through the shared /signin page, whose
// emailed link is verified by the portfolio app's /portfolio/auth callback and
// then returns here — at which point getSession() finds the stored session.

const WORKER_BASE: string = (import.meta as any).env?.DEV
  ? ""
  : ((import.meta as any).env?.VITE_WORKER_URL ?? "https://api.marketheist.io");

const SESSION_KEY = "mh.session.v1";
const LEGACY_SESSION_KEY = "bth.session.v1"; // backtest's key — read-only fallback

export interface Session {
  email: string;
  token: string;
  expiresAt: number; // milliseconds
}

function parse(raw: string | null): Session | null {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Session;
    if (!s.email || !s.token || !s.expiresAt) return null;
    if (Date.now() > s.expiresAt) return null;
    return s;
  } catch {
    return null;
  }
}

/** Send the user to the shared /signin page, returning them here afterward. */
export function gotoAuth(view: "sign-up" | "login" = "sign-up"): void {
  const next = window.location.pathname + window.location.search + window.location.hash;
  window.location.href = `/signin?view=${view}&next=${encodeURIComponent(next)}`;
}

export function getSession(): Session | null {
  const current = parse(localStorage.getItem(SESSION_KEY));
  if (current) return current;
  // SSO fallback: adopt a live backtest session and copy it forward (don't delete).
  const legacy = parse(localStorage.getItem(LEGACY_SESSION_KEY));
  if (legacy) {
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(legacy)); } catch { /* ignore */ }
    return legacy;
  }
  if (localStorage.getItem(SESSION_KEY)) {
    try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  }
  return null;
}

export function clearSession(): void {
  try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(LEGACY_SESSION_KEY); } catch { /* ignore */ }
}

/** Validate the stored token against the worker; clears it on 401. */
export async function getMe(): Promise<{ email: string } | null> {
  const session = getSession();
  if (!session) return null;
  try {
    const res = await fetch(`${WORKER_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${session.token}` },
    });
    if (res.status === 401) {
      clearSession();
      return null;
    }
    if (!res.ok) return null;
    return (await res.json()) as { email: string };
  } catch {
    return null;
  }
}
